/**
 * Store de configuración del usuario
 *
 * Maneja:
 * - Tema (light/dark/system)
 * - Idioma
 * - Accesibilidad
 * - Notificaciones
 *
 * Persistencia:
 * - localStorage para acceso inmediato
 * - Sincroniza con servidor para multi-dispositivo
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { withDevtools } from './middleware-utils';

// ═══════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';
export type Language = 'es' | 'en';
export type FontSize = 'small' | 'medium' | 'large';

const THEME_PREFERENCE_STORAGE_KEY = 'tankef-theme-preference';

interface DisplaySettings {
  theme: Theme;
  language: Language;
  compactMode: boolean;
  showBalances: boolean;
}

interface AccessibilitySettings {
  fontSize: FontSize;
  reducedMotion: boolean;
  highContrast: boolean;
}

interface NotificationSettings {
  email: boolean;
  push: boolean;
  sms: boolean;
  transferAlerts: boolean;
  securityAlerts: boolean; // Este no se puede desactivar por regulación
  marketingEmails: boolean;
}

interface LegalConsentSettings {
  termsAccepted: boolean;
  termsAcceptedAt: string | null; // ISO 8601 — fecha de aceptación para auditoría
}

export interface HelpSettings {
  toursEnabled: boolean; // Auto-show guided tours (default: true)
  helpButtonVisible: boolean; // Show HelpTrigger in page headers (default: false)
}

interface SettingsState {
  display: DisplaySettings;
  accessibility: AccessibilitySettings;
  notifications: NotificationSettings;
  legal: LegalConsentSettings;
  help: HelpSettings;

  // Actions
  setTheme: (theme: Theme) => void;
  setLanguage: (language: Language) => void;
  setFontSize: (size: FontSize) => void;
  setCompactMode: (value: boolean) => void;
  toggleCompactMode: () => void;
  toggleShowBalances: () => void;
  toggleReducedMotion: () => void;
  toggleHighContrast: () => void;
  updateNotifications: (settings: Partial<NotificationSettings>) => void;
  setTermsAccepted: (accepted: boolean) => void;
  updateHelp: (settings: Partial<HelpSettings>) => void;
  resetToDefaults: () => void;
}

// ═══════════════════════════════════════════════════════════════
// DEFAULTS
// ═══════════════════════════════════════════════════════════════

const defaultSettings: Pick<
  SettingsState,
  'display' | 'accessibility' | 'notifications' | 'legal' | 'help'
> = {
  display: {
    theme: 'system',
    language: 'es',
    compactMode: false,
    showBalances: true,
  },
  accessibility: {
    fontSize: 'medium',
    reducedMotion: false,
    highContrast: false,
  },
  notifications: {
    email: true,
    push: true,
    sms: true,
    transferAlerts: true,
    securityAlerts: true, // Siempre true por regulación
    marketingEmails: false,
  },
  legal: {
    termsAccepted: false,
    termsAcceptedAt: null,
  },
  help: {
    toursEnabled: true,
    helpButtonVisible: false,
  },
};

// ═══════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════

export const useSettingsStore = create<SettingsState>()(
  withDevtools(
    'Settings',
    persist(
      set => ({
        ...defaultSettings,

        setTheme: theme =>
          set(state => ({
            display: { ...state.display, theme },
          })),

        setLanguage: language =>
          set(state => ({
            display: { ...state.display, language },
          })),

        setFontSize: fontSize =>
          set(state => ({
            accessibility: { ...state.accessibility, fontSize },
          })),

        setCompactMode: value =>
          set(state => ({
            display: { ...state.display, compactMode: value },
          })),

        toggleCompactMode: () =>
          set(state => ({
            display: { ...state.display, compactMode: !state.display.compactMode },
          })),

        toggleShowBalances: () =>
          set(state => ({
            display: { ...state.display, showBalances: !state.display.showBalances },
          })),

        toggleReducedMotion: () =>
          set(state => ({
            accessibility: {
              ...state.accessibility,
              reducedMotion: !state.accessibility.reducedMotion,
            },
          })),

        toggleHighContrast: () =>
          set(state => ({
            accessibility: {
              ...state.accessibility,
              highContrast: !state.accessibility.highContrast,
            },
          })),

        updateNotifications: settings =>
          set(state => ({
            notifications: {
              ...state.notifications,
              ...settings,
              // Security alerts no se pueden desactivar
              securityAlerts: true,
            },
          })),

        setTermsAccepted: accepted =>
          set(state => ({
            legal: {
              ...state.legal,
              termsAccepted: accepted,
              termsAcceptedAt: accepted ? new Date().toISOString() : null,
            },
          })),

        updateHelp: settings =>
          set(state => ({
            help: { ...state.help, ...settings },
          })),

        resetToDefaults: () => set(defaultSettings),
      }),
      {
        name: 'tankef-settings',
        version: 3,
        storage: createJSONStorage(() => localStorage),
        migrate: (persistedState: unknown, version: number) => {
          const state = persistedState as Record<string, unknown>;
          if (version < 2) {
            state.legal = { termsAccepted: false, termsAcceptedAt: null };
          }
          if (version < 3) {
            state.help = { toursEnabled: true, helpButtonVisible: false };
          }
          return state as unknown as SettingsState;
        },
      }
    )
  )
);

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Aplica el tema al documento
 * Llamar cuando cambia el tema o al iniciar la app
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  const resolvedTheme = resolveTheme(theme);

  root.classList.toggle('dark', resolvedTheme === 'dark');
}

/**
 * Resuelve el tema efectivo para runtime.
 * `system` depende de `prefers-color-scheme`.
 */
export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme !== 'system') {
    return theme;
  }

  if (typeof window === 'undefined') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Guarda la preferencia visible del usuario.
 * El backend solo persiste `light|dark`; `system` vive localmente.
 */
export function storeThemePreference(theme: Theme): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, theme);
}

/**
 * Obtiene la preferencia visible almacenada localmente.
 */
export function getStoredThemePreference(): Theme | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = window.localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY);
  if (value === 'light' || value === 'dark' || value === 'system') {
    return value;
  }

  return null;
}

/**
 * Aplica preferencias de accesibilidad al documento
 */
export function applyAccessibility(settings: AccessibilitySettings): void {
  const root = document.documentElement;

  // Font size
  const fontSizeMap: Record<FontSize, string> = {
    small: '14px',
    medium: '16px',
    large: '18px',
  };
  root.style.fontSize = fontSizeMap[settings.fontSize];

  // Reduced motion
  if (settings.reducedMotion) {
    root.style.setProperty('--animation-duration', '0.01ms');
  } else {
    root.style.removeProperty('--animation-duration');
  }

  // High contrast
  root.classList.toggle('high-contrast', settings.highContrast);
}
